// Building-definition lookups over the compiled defs (machines.json, recipes.json, the shop table). Pure and
// allocation-light: systems call these every tick.
import type { Int } from './core/types.ts';
import {
  isActive,
  type MatcherDef,
  type PackerDef,
  type ProcessorDef,
  type RecipeDef,
  type ShopItemDef,
  type SimDefs,
} from './defs.ts';
import type { BType, FamId, RecipeId, ShopId } from './gen/ids.ts';
import type { Building, ProcessorB, ProcessorType } from './state.ts';

/** Single-recipe processors with an input slot per recipe input (R2). */
export type SimpleProcType = 'caster' | 'grinder' | 'smelter' | 'press';
/** Laggard-climb matchers (R4). */
export type MatcherType = 'loader' | 'gunsmith';

export function isSimpleProc(t: BType): t is SimpleProcType {
  return t === 'caster' || t === 'grinder' || t === 'smelter' || t === 'press';
}

export function isMatcher(t: BType): t is MatcherType {
  return t === 'loader' || t === 'gunsmith';
}

export function isProcessorType(t: BType): t is ProcessorType {
  return isSimpleProc(t) || isMatcher(t);
}

/** Machines in the R2 sense (a front arrow, work per tick): processors and the Packer. */
export function isMachineType(t: BType): boolean {
  return isProcessorType(t) || t === 'packer';
}

/** Sinks accept on all four sides (GDD §4.4): Crate, Gallery, Dock. */
export function isSinkType(t: BType): boolean {
  return t === 'crate' || t === 'gallery' || t === 'dock';
}

export function simpleDef(d: SimDefs, t: SimpleProcType): ProcessorDef {
  return d.buildings[t];
}

export function matcherDef(d: SimDefs, t: MatcherType): MatcherDef {
  return d.buildings[t];
}

export function packerDef(d: SimDefs): PackerDef {
  return d.buildings.packer;
}

export function recipeById(d: SimDefs, id: RecipeId): RecipeDef {
  for (const r of d.recipes) if (r.id === id) return r;
  throw new RangeError(`unknown recipe ${id}`);
}

/** The first version-active mode of a simple processor (v0.1: cast, grind, copper, casing). */
export function defaultMode(d: SimDefs, t: SimpleProcType): string {
  for (const m of simpleDef(d, t).modes) if (isActive(d, m.version)) return m.id;
  throw new RangeError(`${t} has no active mode`);
}

/** The recipe a processor runs now (simple: its mode's first recipe; matcher: its recipe). */
export function recipeOf(d: SimDefs, b: ProcessorB): RecipeDef {
  if (isMatcher(b.type)) return recipeById(d, matcherDef(d, b.type).recipe);
  const t = b.type as SimpleProcType;
  for (const m of simpleDef(d, t).modes) {
    if (m.id === b.mode) {
      const id = m.recipes[0];
      if (id === undefined) break;
      return recipeById(d, id);
    }
  }
  throw new RangeError(`${b.type} ${b.id}: no recipe for mode ${b.mode}`);
}

/** Input slot families in fixed order (matchers: the def's slot order; simple: the recipe inputs). */
export function slotFams(d: SimDefs, b: ProcessorB): readonly FamId[] {
  if (isMatcher(b.type)) return matcherDef(d, b.type).slots;
  return recipeOf(d, b).inputs;
}

/** Items per input slot (simple: inCap; matchers: slotCap). */
export function slotCap(d: SimDefs, t: ProcessorType): Int {
  return isMatcher(t) ? matcherDef(d, t).slotCap : simpleDef(d, t as SimpleProcType).inCap;
}

/** R2 cycle cost in work units. */
export function cycleCost(d: SimDefs, t: ProcessorType): Int {
  return isMatcher(t) ? matcherDef(d, t).cost : simpleDef(d, t as SimpleProcType).cost;
}

/** Work per tick at a level (R2). */
export function workPerTick(d: SimDefs, level: Int): Int {
  const w = d.machineRules.workPerTick[level - 1];
  if (w === undefined) throw new RangeError(`no work per tick for level ${level}`);
  return w;
}

/** Highest level a building type reaches by merging. */
export function maxLevelOf(d: SimDefs, b: Pick<Building, 'type'>): Int {
  switch (b.type) {
    case 'deposit':
      return d.depositRules.oddsByLevel.length;
    case 'drill':
      return d.buildings.drill.hitTicks.length;
    case 'crate':
      return d.buildings.crate.capacity.length;
    case 'chute':
      return 1;
    case 'gallery':
      return d.buildings.gallery.gunCapByLevel.length;
    default:
      return d.machineRules.maxLevel;
  }
}

export function shopItem(d: SimDefs, id: ShopId): ShopItemDef {
  for (const x of d.shop) if (x.id === id) return x;
  throw new RangeError(`unknown shop item ${id}`);
}

/** Packer target cap (R5): by level, limited by the locked family's maximum (ammo when unlocked). */
export function packerCap(d: SimDefs, level: Int, lockFam: FamId | null): Int {
  const byLevel = packerDef(d).targetCapByLevel[level - 1] ?? 1;
  const fam = lockFam ?? 'ammo';
  let famMax = 1;
  for (const f of d.families) if (f.id === fam) famMax = f.maxTier;
  return byLevel < famMax ? byLevel : famMax;
}

/** Tier count of the widest family over all versions (Crate `counts` length). */
export function crateTiers(d: SimDefs): Int {
  return d.tierUnits.length;
}
